import type { SessionListing } from "@meleeroyale/protocol";
import { ROOM_RULES } from "@meleeroyale/shared";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import {
  createSession,
  joinSession,
  listSessions,
  loadNickname,
  startPractice,
} from "../app/store.ts";
import {
  Alert,
  Badge,
  Empty,
  Field,
  FieldGroup,
  Icon,
  NumberTiles,
  Skeleton,
  Stepper,
  Tabs,
} from "../components/ui/index.tsx";
import { fmt, fmtCount, t } from "../i18n/index.ts";

type Mode = "practice" | "create" | "join";
const MODE_KEY = "meleeroyale.menu";
const POLL_MS = 4000;

const MODES = [
  { value: "practice", label: t.lobby.modes.practice, icon: "bot" },
  { value: "create", label: t.lobby.modes.create, icon: "plus" },
  { value: "join", label: t.lobby.modes.join, icon: "users" },
] as const;

function loadMode(): Mode {
  try {
    const m = localStorage.getItem(MODE_KEY);
    return m === "create" || m === "join" ? m : "practice";
  } catch {
    return "practice";
  }
}

function saveMode(mode: Mode): void {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    /* private mode: the tab just will not be remembered */
  }
}

export function errorText(code: string): string {
  const errors: Record<string, string> = t.lobby.errors;
  return errors[code] ?? t.lobby.errors.generic;
}

export function Lobby({ notice }: { notice: string | null }) {
  const [nickname, setNickname] = useState(loadNickname);
  const [mode, setMode] = useState<Mode>(loadMode);
  const name = nickname.trim();
  const valid = name.length >= 1 && name.length <= 16;

  return (
    <main className="shell">
      <div className="shell-center stack stack-6">
        <header className="lobby-hero stack stack-3">
          <h1 className="lobby-title">{t.app.title}</h1>
          <p className="lobby-tagline">{t.lobby.tagline}</p>
        </header>

        <section className="card stack stack-5">
          <Field label={t.lobby.nickname} hint={t.lobby.nicknameHint} htmlFor="nickname">
            <input
              id="nickname"
              className="input"
              value={nickname}
              maxLength={16}
              autoComplete="nickname"
              placeholder={t.lobby.nicknamePlaceholder}
              onChange={(e) => setNickname(e.target.value)}
            />
          </Field>
          <Tabs
            label={t.lobby.modesLabel}
            value={mode}
            options={MODES}
            onChange={(next) => {
              setMode(next);
              saveMode(next);
            }}
          />
          {notice ? <Alert kind="info">{errorText(notice)}</Alert> : null}
          <div role="tabpanel" className="stack stack-5">
            {mode === "practice" ? (
              <PracticePanel nickname={name} valid={valid} />
            ) : mode === "create" ? (
              <CreatePanel nickname={name} valid={valid} />
            ) : (
              <JoinPanel nickname={name} valid={valid} />
            )}
          </div>
        </section>

        <section className="lobby-rules" aria-label={t.app.title}>
          <div className="rule">
            <Icon name="cage" />
            <div>
              <strong>{t.lobby.rules.cage.title}</strong>
              <span>{t.lobby.rules.cage.body}</span>
            </div>
          </div>
          <div className="rule">
            <Icon name="fist" />
            <div>
              <strong>{t.lobby.rules.limbs.title}</strong>
              <span>{t.lobby.rules.limbs.body}</span>
            </div>
          </div>
          <div className="rule">
            <Icon name="clock" />
            <div>
              <strong>{t.lobby.rules.clock.title}</strong>
              <span>{t.lobby.rules.clock.body}</span>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

function Busy() {
  return (
    <div className="stack stack-3" role="status" aria-label={t.common.loading}>
      <Skeleton height={20} width="40%" />
      <Skeleton height={48} />
      <Skeleton height={56} />
      <p className="muted">{t.lobby.requesting}</p>
    </div>
  );
}

function PracticePanel({ nickname, valid }: { nickname: string; valid: boolean }) {
  const [bots, setBots] = useState(2);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (busy) return <Busy />;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    const code = await startPractice(nickname, bots);
    if (code) {
      setError(code);
      setBusy(false);
    }
  };
  return (
    <form className="stack stack-5" onSubmit={submit}>
      <p className="muted">{t.lobby.practice.intro}</p>
      <FieldGroup label={t.lobby.practice.bots} hint={t.lobby.practice.botsHint}>
        <NumberTiles
          value={bots}
          from={ROOM_RULES.practice.minBots}
          to={ROOM_RULES.practice.maxBots}
          onChange={setBots}
        />
      </FieldGroup>
      {error ? <Alert kind="danger">{errorText(error)}</Alert> : null}
      <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={!valid}>
        <Icon name="fist" />
        {t.lobby.practice.submit}
      </button>
    </form>
  );
}

function CreatePanel({ nickname, valid }: { nickname: string; valid: boolean }) {
  const [players, setPlayers] = useState<number>(ROOM_RULES.custom.minHumans);
  const [bots, setBots] = useState(0);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pinOk = pin.length === 0 || pin.length === 4;
  if (busy) return <Busy />;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || !pinOk) return;
    setBusy(true);
    setError(null);
    const code = await createSession(nickname, players, bots, pin.length === 4 ? pin : null);
    if (code) {
      setError(code);
      setBusy(false);
    }
  };
  return (
    <form className="stack stack-5" onSubmit={submit}>
      <p className="muted">{t.lobby.create.intro}</p>
      <div className="form-grid">
        <FieldGroup label={t.lobby.create.players} hint={t.lobby.create.playersHint}>
          <Stepper
            id="create-players-value"
            value={players}
            min={ROOM_RULES.custom.minHumans}
            max={ROOM_RULES.custom.maxHumans}
            onChange={setPlayers}
            fewerLabel={t.lobby.fewer}
            moreLabel={t.lobby.more}
          />
        </FieldGroup>
        <Field
          label={t.lobby.create.pin}
          hint={pinOk ? t.lobby.create.pinHint : t.lobby.create.pinIncomplete}
          htmlFor="create-pin"
        >
          <input
            id="create-pin"
            className="input input-pin num"
            value={pin}
            inputMode="numeric"
            autoComplete="off"
            maxLength={4}
            placeholder={t.lobby.create.pinPlaceholder}
            aria-invalid={!pinOk}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
          />
        </Field>
      </div>
      <FieldGroup label={t.lobby.create.bots} hint={t.lobby.create.botsHint}>
        <NumberTiles value={bots} from={0} to={ROOM_RULES.custom.maxBots} onChange={setBots} />
      </FieldGroup>
      {error ? <Alert kind="danger">{errorText(error)}</Alert> : null}
      <button
        type="submit"
        className="btn btn-primary btn-lg btn-block"
        disabled={!valid || !pinOk}
      >
        <Icon name="users" />
        {t.lobby.create.submit}
      </button>
    </form>
  );
}

type ListState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; sessions: SessionListing[] };

function JoinPanel({ nickname, valid }: { nickname: string; valid: boolean }) {
  const [list, setList] = useState<ListState>({ status: "loading" });
  const [refreshing, setRefreshing] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ roomId: string; code: string } | null>(null);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const sessions = await listSessions();
      setList({ status: "ready", sessions });
    } catch {
      setList((prev) => (prev.status === "ready" ? prev : { status: "error" }));
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = setInterval(() => void refresh(), POLL_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  const join = async (s: SessionListing, e?: FormEvent) => {
    e?.preventDefault();
    if (!valid || busy) return;
    if (s.locked && (open !== s.roomId || pin.length !== 4)) {
      if (open !== s.roomId) {
        setOpen(s.roomId);
        setPin("");
        setRowError(null);
      }
      return;
    }
    setBusy(s.roomId);
    setRowError(null);
    const code = await joinSession(nickname, s.roomId, s.locked ? pin : null);
    if (code) {
      setRowError({ roomId: s.roomId, code });
      setBusy(null);
      if (code !== "bad_pin") void refresh();
    }
  };

  return (
    <div className="stack stack-4">
      <div className="section-head">
        <h2>{t.lobby.join.title}</h2>
        <button
          type="button"
          className="btn btn-ghost btn-small"
          onClick={() => void refresh()}
          disabled={refreshing}
        >
          <Icon name="refresh" />
          {t.lobby.join.refresh}
        </button>
      </div>
      {list.status === "loading" ? (
        <div className="stack stack-2" role="status" aria-label={t.common.loading}>
          <Skeleton height={64} />
          <Skeleton height={64} />
        </div>
      ) : list.status === "error" ? (
        <Alert kind="danger">{t.lobby.join.error}</Alert>
      ) : list.sessions.length === 0 ? (
        <Empty icon="users" title={t.lobby.join.empty} hint={t.lobby.join.emptyHint} />
      ) : (
        <ul className="session-list">
          {list.sessions.map((s) => {
            const full = s.humans >= s.maxHumans;
            const pinOpen = s.locked && open === s.roomId;
            const error = rowError?.roomId === s.roomId ? rowError.code : null;
            return (
              <li key={s.roomId} className="session-row">
                <form className="session-grid" onSubmit={(e) => void join(s, e)}>
                  <div className="session-main">
                    <strong className="session-host">
                      {fmt(t.lobby.join.host, { name: s.host })}
                    </strong>
                    <span className="session-meta">
                      <span className="num">
                        {fmt(t.lobby.join.players, { humans: s.humans, min: s.minHumans })}
                      </span>
                      <span aria-hidden="true">·</span>
                      <span>
                        {s.bots > 0 ? fmtCount(t.lobby.join.bots, s.bots) : t.lobby.join.noBots}
                      </span>
                      {s.humans >= s.minHumans ? (
                        <Badge kind="ok">{t.lobby.join.ready}</Badge>
                      ) : null}
                      {full ? <Badge kind="danger">{t.lobby.join.full}</Badge> : null}
                      {s.locked ? (
                        <Badge>
                          <Icon name="lock" />
                          {t.lobby.join.locked}
                        </Badge>
                      ) : null}
                    </span>
                  </div>
                  <div className="session-actions">
                    {pinOpen ? (
                      <input
                        className="input input-pin num"
                        aria-label={t.lobby.join.pin}
                        placeholder={t.lobby.create.pinPlaceholder}
                        value={pin}
                        inputMode="numeric"
                        autoComplete="off"
                        maxLength={4}
                        // biome-ignore lint/a11y/noAutofocus: the field appears because the player asked to type into it
                        autoFocus
                        onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      />
                    ) : null}
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={!valid || full || busy !== null || (pinOpen && pin.length !== 4)}
                      data-busy={busy === s.roomId || undefined}
                    >
                      {s.locked ? <Icon name="lock" /> : <Icon name="play" />}
                      {t.lobby.join.submit}
                    </button>
                  </div>
                  {error ? (
                    <div className="session-error">
                      <Alert kind="danger">{errorText(error)}</Alert>
                    </div>
                  ) : null}
                </form>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
